import React, {FC} from 'react';

/** 交付政策英文正文（与 content/zh 结构 1:1，类名复用 TermsOfUse__* BEM） */
const EnContent: FC = () => {
  return (
    <>
      <p className="v3-doc-meta">Effective Date: August 28, 2026</p>
      <p className="v3-doc-meta">Last Updated: August 28, 2026</p>
      <p>Thank you for choosing Xiangji services.</p>
      <p>
        This Xiangji Service Delivery Policy (hereinafter referred to as “this Policy”) describes how Xiangji delivers
        its software-as-a-service (SaaS), AI visual content processing, and other digital services to users, including
        the delivery methods, delivery times, service activation, and related matters.
      </p>
      <p>
        <strong>
          All services provided by Xiangji are digital services and generally do not involve the production, storage, or
          logistics shipping of physical goods.
        </strong>
      </p>
      <section>
        <h2>1. Delivery of Digital Services</h2>
        <h3>Nature of Services</h3>
        <p>
          Xiangji primarily provides digital products and services to users over the internet, including but not limited
          to:
        </p>
        <ul>
          <li>Image translation;</li>
          <li>Image editing and retouching;</li>
          <li>AI image generation;</li>
          <li>Product scene image generation;</li>
          <li>Product model image generation;</li>
          <li>Product main and secondary image generation;</li>
          <li>Product detail image set generation;</li>
          <li>Product detail page and A+ content generation;</li>
          <li>Video and visual content processing;</li>
          <li>File and PSD processing;</li>
          <li>API and other technical services;</li>
          <li>Subscription services and related membership benefits;</li>
          <li>Points, credits, and other digital service benefits.</li>
        </ul>
        <p>
          <strong>
            All of the above services are delivered through the Xiangji website, client applications, browser
            extensions, APIs, or other online means, and no physical goods shipping is provided.
          </strong>
        </p>
      </section>
      <section>
        <h2>2. Service Delivery Methods</h2>
        <h3>Account Delivery</h3>
        <p>
          For services that require account registration, once you have completed registration or login and the
          corresponding service has been successfully activated, you can access the relevant services through your
          Xiangji account.
        </p>
        <h3>Subscription Service Delivery</h3>
        <p>
          For subscription services, once you have completed payment and the order is successfully confirmed, the
          relevant subscription benefits are usually bound automatically to your Xiangji account.
        </p>
        <p>
          <strong>
            You do not need to wait for physical shipment or logistics delivery; subscription services are usually
            available as soon as the order is completed.
          </strong>
        </p>
        <p>
          The specific subscription period, scope of benefits, usage quotas, and validity period are subject to the
          purchase page and the relevant subscription agreement.
        </p>
        <h3>Credits and Quota Delivery</h3>
        <p>
          For the points, Credits, usage quotas, or other digital benefits provided by Xiangji, the relevant benefits
          are usually granted automatically to your Xiangji account after successful payment and order confirmation.
        </p>
        <p>
          You can view the corresponding benefits in your account balance, order records, or the relevant service pages.
        </p>
        <h3>One-Time Service Delivery</h3>
        <p>
          For services billed per task or by volume, such as image translation, image editing, AI image generation, and
          detail image set generation, after you submit a task and complete the corresponding payment, Xiangji will
          process your task according to the service workflow displayed on the product page.
        </p>
        <p>
          <strong>
            The generated results, processed outputs, or other digital deliverables are usually delivered directly
            through the Xiangji product interface, and you can view, download, or further edit them once the task is
            completed.
          </strong>
        </p>
        <h3>API Services</h3>
        <p>
          For API services, after you have completed account registration, API service activation, and the necessary
          configuration, Xiangji will provide you with API access in accordance with the relevant API service agreement
          or technical documentation.
        </p>
        <p>
          The specific delivery method, call limits, quotas, billing methods, and technical requirements of API services
          are subject to the API service pages and the relevant agreements.
        </p>
      </section>
      <section>
        <h2>3. Delivery Time</h2>
        <h3>Automatic Delivery</h3>
        <p>
          Unless otherwise stated on the product pages, the standard digital services provided by Xiangji are usually
          delivered in an automated manner.
        </p>
        <p>
          <strong>
            After successful payment, order confirmation, and any necessary security verification, the relevant services
            are usually activated immediately or within a reasonable time.
          </strong>
        </p>
        <h3>AI Generation and Processing Services</h3>
        <p>
          AI image generation, image translation, image editing, video processing, and other compute-intensive services
          require system processing.
        </p>
        <p>
          Depending on task complexity, file size, server load, AI model processing speed, and other technical factors,{' '}
          <strong>the actual processing time may vary.</strong>
        </p>
        <p>
          The estimated processing time displayed on product pages is for reference only and does not constitute an
          absolute commitment to a specific completion time.
        </p>
        <h3>Manual or Special Services</h3>
        <p>
          If a service requires manual review, manual processing, special configuration, or other additional operations,
          the specific delivery time is subject to the service page, the order page, or a separate agreement between the
          parties.
        </p>
      </section>
      <section>
        <h2>4. Order and Payment Confirmation</h2>
        <h3>Successful Payment</h3>
        <p>
          After you complete the payment for an order, the payment institution may need some time to confirm the
          transaction.
        </p>
        <p>
          <strong>
            Only after Xiangji’s system confirms that the order payment was successful will the relevant digital service
            benefits be delivered in accordance with this Policy.
          </strong>
        </p>
        <h3>Payment Exceptions</h3>
        <p>
          If you have completed payment but your Xiangji account does not show a successful order, the service benefits
          have not been credited, or any other exception occurs, please contact Xiangji customer service promptly.
        </p>
        <p>
          Xiangji will handle the matter based on the actual circumstances after verifying the order and payment status.
        </p>
        <h3>Third-Party Payments</h3>
        <p>
          If you complete payment through a third-party payment institution, the institution may be subject to
          transaction review, payment delays, refund processing, or other technical issues.
        </p>
        <p>
          For payment delays or exceptions caused by the third-party payment institution itself, Xiangji will assist you
          in resolving them within a reasonable scope.
        </p>
      </section>
      <section>
        <h2>5. Account and Service Access</h2>
        <h3>Account Responsibility</h3>
        <p>
          <strong>
            You should ensure that the Xiangji account information used for registration and purchase is accurate and
            valid, and keep your account and login credentials safe.
          </strong>
        </p>
        <p>Service benefits are usually bound to the Xiangji account used at the time of purchase.</p>
        <h3>Incorrect Account</h3>
        <p>
          If you purchase services with the wrong account due to your own reasons, causing the relevant service benefits
          to be bound to another account, you should contact customer service promptly for verification.
        </p>
        <p>
          <strong>
            To the extent permitted by law, Xiangji shall not be liable for losses arising from service benefit
            anomalies caused by your own operational errors.
          </strong>
        </p>
        <h3>Account Sharing</h3>
        <p>
          Except for the enterprise edition, multi-user collaboration, or other team features explicitly supported by
          Xiangji, you may not provide services to others by renting, lending, transferring, or sharing accounts.
        </p>
      </section>
      <section>
        <h2>6. Delivery Exceptions</h2>
        <p>If you encounter any of the following situations, please contact Xiangji customer service promptly:</p>
        <ul>
          <li>Payment succeeded but the service was not activated;</li>
          <li>Points or credits were not credited;</li>
          <li>Subscription benefits did not take effect;</li>
          <li>AI tasks have remained incomplete for an extended period;</li>
          <li>Completed tasks cannot be viewed or downloaded properly;</li>
          <li>API services were not activated properly;</li>
          <li>Other exceptions related to service delivery.</li>
        </ul>
        <p>
          After verifying the order, account, and system records, Xiangji will, within a reasonable scope, re-deliver
          the benefits, reprocess the tasks, restore the services, or take other reasonable measures.
        </p>
        <h3>System Causes</h3>
        <p>
          <strong>
            If a Xiangji system failure causes the service benefits you actually purchased to be delivered incorrectly,
            and the relevant order and payment records can be effectively verified, Xiangji will re-deliver the relevant
            benefits or take other reasonable remedial measures based on the actual circumstances.
          </strong>
        </p>
        <h3>User Causes</h3>
        <p>
          Xiangji shall not be liable, to the extent permitted by law, for delivery exceptions caused by the following:
        </p>
        <ul>
          <li>The user provided incorrect account information;</li>
          <li>The user modified or deleted the relevant account information on their own;</li>
          <li>The user’s account was banned or restricted;</li>
          <li>The user’s device, network, or software environment was abnormal;</li>
          <li>The user failed to complete the required configuration as required by the service;</li>
          <li>Other cases where the service cannot be used normally due to the user’s own reasons.</li>
        </ul>
      </section>
      <section>
        <h2>7. Service Unavailability and Third-Party Services</h2>
        <p>
          Some Xiangji products and features rely on cloud computing services, AI models, translation engines, network
          providers, payment institutions, and other third-party technical services.
        </p>
        <p>
          <strong>
            Failures, maintenance, upgrades, capacity limits, interface adjustments, or other causes beyond Xiangji’s
            reasonable control involving third-party services may result in service delays, interruptions, or temporary
            unavailability.
          </strong>
        </p>
        <p>
          In such cases, Xiangji will take reasonable measures based on the actual circumstances, including but not
          limited to:
        </p>
        <ul>
          <li>Restoring the relevant services;</li>
          <li>Switching technical services;</li>
          <li>Delaying task processing;</li>
          <li>Reprocessing the relevant tasks;</li>
          <li>Re-delivering the corresponding service benefits;</li>
          <li>Other reasonable remedial measures.</li>
        </ul>
      </section>
      <section>
        <h2>8. Service Downloads and Data Retention</h2>
        <h3>Downloads</h3>
        <p>
          For AI-generated results, translation results, editing results, and other digital content that support
          downloads, you can download or export them in the manner provided on the Xiangji product pages.
        </p>
        <h3>User Backup Responsibility</h3>
        <p>
          <strong>
            You are responsible for backing up in a timely manner the generated results, processed files, project data,
            and other important content that you need to retain long-term.
          </strong>
        </p>
        <p>
          Except as otherwise provided by laws and regulations or agreed by the parties, Xiangji does not promise to
          permanently store data whose services have been terminated, whose accounts have been deleted, or that has
          exceeded the platform’s prescribed retention period.
        </p>
        <h3>Data After Service Termination</h3>
        <p>
          If your account is deregistered, the service is terminated, or the relevant project exceeds the platform’s
          prescribed retention period, the relevant data may be deleted or handled in accordance with the Xiangji
          Privacy Policy.
        </p>
      </section>
      <section>
        <h2>9. Determination of Delivery Completion</h2>
        <p>In principle, the relevant digital services are deemed delivered once any of the following occurs:</p>
        <ol>
          <li>Subscription benefits have been successfully bound to your Xiangji account;</li>
          <li>Points, Credits, or other digital benefits have been successfully credited;</li>
          <li>API access has been successfully activated;</li>
          <li>The AI generation or processing task has been completed and can be viewed or downloaded properly;</li>
          <li>The relevant services have been provided to your account as agreed on the product pages;</li>
          <li>Other delivery conditions agreed by both parties have been fulfilled.</li>
        </ol>
        <p>
          <strong>
            For digital services that have been delivered or actually used, refunds and related handling are governed by
            the Xiangji Refund Policy, the Xiangji Subscription Purchase Agreement, and other applicable rules.
          </strong>
        </p>
      </section>
      <section>
        <h2>10. Changes to This Policy</h2>
        <p>
          <strong>
            Xiangji reserves the right to modify or supplement this Delivery Policy in light of changes in laws and
            regulations, business development, product adjustments, and technical upgrades.
          </strong>
        </p>
        <p>
          If material changes occur to this Policy, Xiangji will notify you through website announcements, product
          pages, pop-ups, in-app notices, or other reasonable means.
        </p>
        <p>The revised Policy takes effect on the date of publication or the specified effective date.</p>
      </section>
      <section>
        <h2>11. Contact Us</h2>
        <p>
          If you have any questions about service delivery, order status, service activation, or other related matters,
          you can contact us in the following ways:
        </p>
        <ul>
          <li>
            <strong>Official website: </strong>
            <a href="https://www.xiangjifanyi.com/" rel="noreferrer" target="_blank">
              https://www.xiangjifanyi.com/
            </a>
          </li>
          <li>
            <strong>Online customer service: </strong>online customer service within the Xiangji product
          </li>
          <li>
            <strong>Customer service email: </strong>
            <a href="mailto:bussiness@tosoiot.com">bussiness@tosoiot.com</a>
          </li>
          <li>
            <strong>Phone: </strong>+86 176 6407 5341
          </li>
        </ul>
        <p>We will handle your inquiries within a reasonable period after receiving them.</p>
        <p>
          <strong>
            For matters not covered in this Policy, the Xiangji Terms of Use, the Xiangji Privacy Policy, the Xiangji
            Subscription Purchase Agreement, and other applicable service rules shall prevail.
          </strong>
        </p>
      </section>
    </>
  );
};

export default EnContent;
